/**
 * O menu do usuário no cabeçalho (11.1/11.2).
 *
 * ## Três buracos, um componente
 *
 * O cabeçalho dizia a empresa e nada mais: quem estava logado só aparecia no
 * "BEM-VINDO, NOME", o papel não aparecia em lugar nenhum, e o e-mail — a única marca
 * que distingue duas contas da mesma pessoa — não aparecia no portal inteiro.
 *
 * ## A troca de PORTAL deixa de ser uma TELA (D-131)
 *
 * Quem entra em mais de um portal tinha um botão "Trocar" que desmontava a página
 * inteira para mudar um contexto que a sessão troca em um pedido. Aqui a lista está no
 * próprio menu, com marca no item corrente, e a troca acontece no lugar. A EMPRESA, dentro
 * do portal, não mora aqui: ela é recorte da página, e o seletor dela fica junto do título.
 *
 * A lista só existe quando há mais de um portal: uma lista de um item é pedir uma
 * decisão que não existe.
 */
import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, LogOut } from 'lucide-react'
import type { EuExternoDoPortal, PortalDoExterno } from '@dv/contracts'
import { nomeDaVisao, quantasEmpresas } from '../formato.js'

/** "You.Ad" vira "YA": a inicial das duas primeiras palavras do nome. */
function iniciaisDe(nome: string): string {
  const palavras = nome.split(/[^\p{L}\p{N}]+/u).filter((parte) => parte !== '')
  const letras = palavras
    .slice(0, 2)
    .map((parte) => parte.slice(0, 1))
    .join('')
    .toUpperCase()
  return letras === '' ? '?' : letras
}

export function MenuDoUsuario({
  eu,
  portal,
  trocarPara,
  sair,
  focarAoMontar = false,
}: {
  eu: EuExternoDoPortal
  portal: PortalDoExterno
  trocarPara: (portal: PortalDoExterno) => void
  sair: () => void
  focarAoMontar?: boolean
}) {
  const [aberto, setAberto] = useState(false)
  const caixa = useRef<HTMLDivElement>(null)
  const gatilho = useRef<HTMLButtonElement>(null)
  useEffect(() => { if (focarAoMontar) gatilho.current?.focus() }, [focarAoMontar])

  useEffect(() => {
    if (!aberto) return undefined
    const porClique = (evento: MouseEvent): void => {
      const alvo = evento.target
      if (!(alvo instanceof Node) || caixa.current?.contains(alvo) !== true) setAberto(false)
    }
    const porTecla = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') { setAberto(false); gatilho.current?.focus() }
    }
    document.addEventListener('mousedown', porClique)
    document.addEventListener('keydown', porTecla)
    return () => {
      document.removeEventListener('mousedown', porClique)
      document.removeEventListener('keydown', porTecla)
    }
  }, [aberto])

  return (
    <div className="menu-usuario" ref={caixa}>
      {/*
        O nome acessível é ESCRITO, e não herdado do conteúdo.

        O avatar e o chevron são `aria-hidden` (iniciais e seta são desenho), e no
        celular a linha da visão some. Sem esta etiqueta, o botão que abre a conta
        inteira chega ao leitor de tela como "botão", sem nome nenhum. Ela diz o PORTAL:
        a empresa, quando há várias, se diz no seletor da página.
      */}
      <button
        type="button"
        className="menu-usuario-botao"
        ref={gatilho}
        aria-haspopup="true"
        aria-expanded={aberto}
        aria-label={`Conta de ${eu.nome}, no portal ${portal.nome}`}
        onClick={() => {
          setAberto((estava) => !estava)
        }}
        data-teste="menu-do-usuario"
      >
        <span className="avatar" aria-hidden="true">
          {iniciaisDe(portal.nome)}
        </span>
        <span className="quem">
          <strong>{portal.nome}</strong>
          <small>
            {eu.nome} · {nomeDaVisao(portal.visao)}
          </small>
        </span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>

      {aberto && (
        <div className="menu-usuario-lista" data-teste="menu-aberto">
          <p className="menu-identidade">
            <strong>{eu.nome}</strong>
            <small>{eu.email}</small>
          </p>

          {eu.portais.length > 1 && (
            <>
              <p className="eyebrow">Portal</p>
              {eu.portais.map((item) => {
                const atual = item.id === portal.id
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="menu-empresa"
                    {...(atual ? { 'aria-current': true } : {})}
                    onClick={() => {
                      setAberto(false)
                      if (!atual) trocarPara(item)
                      gatilho.current?.focus()
                    }}
                  >
                    {atual ? <Check size={16} aria-hidden="true" /> : <span aria-hidden="true" />}
                    <span>
                      <strong>{item.nome}</strong>
                      <small>
                        {nomeDaVisao(item.visao)} · {quantasEmpresas(item.empresas.length)}
                      </small>
                    </span>
                  </button>
                )
              })}
            </>
          )}

          <button type="button" className="menu-sair" onClick={sair} data-teste="sair">
            <LogOut size={16} aria-hidden="true" />
            <span>Sair</span>
          </button>
        </div>
      )}
    </div>
  )
}
